'use client';

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface BackgroundBeamsProps {
  className?: string;
  opacity?: number;
}

export const BackgroundBeams = React.memo(({ className, opacity = 0.4 }: BackgroundBeamsProps) => {
  const numberOfLines = 50;
  const lineSpacing = 10; // 10px spacing between lines

  const paths = useMemo(() => {
    return Array.from({ length: numberOfLines }, (_, i) => {
      const yOffset = i * lineSpacing;
      return `M0 ${yOffset} Q 250 ${yOffset + 100}, 500 ${yOffset} T 1000 ${yOffset}`;
    });
  }, [numberOfLines]);

  return (
    <div
      className={cn(
        'absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden',
        className
      )}
    >
      <svg
        className="absolute h-full w-[1000px] sm:w-full"
        viewBox={`0 0 1000 ${numberOfLines * lineSpacing}`}
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask id="fade-mask" maskContentUnits="objectBoundingBox">
          <linearGradient id="fade-gradient" gradientUnits="objectBoundingBox" x2="0" y2="1">
            <stop offset="0" stopColor="white" stopOpacity="0" />
            <stop offset="0.2" stopColor="white" stopOpacity="1" />
            <stop offset="0.8" stopColor="white" stopOpacity="1" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <rect width="1" height="1" fill="url(#fade-gradient)" />
        </mask>
        <g mask="url(#fade-mask)">
          {paths.map((path, index) => (
            <path
              key={index}
              d={path}
              stroke="url(#line-gradient)"
              strokeOpacity={opacity}
              strokeWidth="1"
            />
          ))}
        </g>
        <defs>
          <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0" />
            <stop offset="50%" stopColor="var(--color-primary)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
});

BackgroundBeams.displayName = 'BackgroundBeams';

export default BackgroundBeams;
